/**
 * user.js — DropPin session / user helper. v0.26.0.
 *
 * Auth is delegated to InSpec via /api/auth-login (the API folder's
 * thin proxy). We persist the returned user payload in localStorage
 * under `droppin.user.v1`. No password / hash is stored — only the
 * sanitised user object.
 */

// v0.x — Capacitor native shell: the HttpOnly cookie never rides from
// capacitor://localhost, so the wrapped app asks login/signup/reset for a
// token session (the SAME InSite-ID JWT the cookie would hold) and stores it
// via Capacitor Preferences; every API call then carries it as
// Authorization: Bearer (see src/utils/nativeShell.js fetch rewrite). On the
// web isNativeShell() is false and NOTHING changes — cookies remain the only
// path.
import { isNativeShell, setNativeToken, clearNativeToken } from './nativeShell.js';
// v0.246.0 — one sign-in across the suite (utils/suiteSignIn.js, the same file
// in every app): a sign-in here is handed on to Synerflo ID.
import { currentPath, handOver, rememberAsked } from './suiteSignIn.js';

const STORAGE_KEY = 'droppin.user.v1';

// Native shell: persist the session token the API handed back in the body,
// then STRIP it from the payload — it must never sit in the localStorage
// user cache or ride into React state. No-op on the web (the server only
// includes sessionToken when asked with tokenSession: true).
async function absorbSessionToken(raw) {
  if (raw && raw.sessionToken) {
    if (isNativeShell()) {
      try { await setNativeToken(raw.sessionToken); } catch (_e) { /* login still valid via cookie */ }
    }
    delete raw.sessionToken;
    delete raw.sessionExpiresAt;
  }
  return raw;
}

function sanitise(u) {
  if (!u) return null;
  // eslint-disable-next-line no-unused-vars
  const { password, PasswordHash, ...safe } = u;
  return safe;
}

/**
 * POST credentials, persist on success.
 *   Returns { user } on success, { mfa } when the password was right and the
 *   authenticator code is needed next (two-step sign-in, InSite-ID v0.48.0),
 *   { error } on failure.
 */
export async function signIn(username, password) {
  return postSignIn({ username, password }, false);
}

/** The second step: the challenge from signIn's { mfa } plus { code } or { recoveryCode }. */
export async function signInSecondStep(mfaToken, factor) {
  return postSignIn({ mfaToken, ...(factor || {}) }, true);
}

async function postSignIn(fields, secondStep) {
  let resp;
  try {
    resp = await fetch('/api/auth-login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...fields,
        // Native shell only: ask for the session token in the response body.
        // A browser says where a sign-in handed on to Synerflo ID comes back to.
        ...(isNativeShell() ? { tokenSession: true } : { returnTo: currentPath() }),
      }),
    });
  } catch (_e) {
    return { error: "Can't reach the DropPin server. Check your connection and try again." };
  }
  if (!resp.ok) {
    // 2026-09-28 — a wrong or expired CODE and a throttle carry the server's
    // own words; a wrong PASSWORD keeps its old wording.
    let msg = '';
    try { const j = await resp.json(); msg = (j && j.error) || ''; } catch (_) {}
    if (resp.status === 401) return { error: secondStep ? (msg || "That code didn't work.") : 'Invalid username or password.', expired: /expired/i.test(msg) };
    if (resp.status === 429) return { error: msg || 'Too many sign-in attempts. Wait a few minutes and try again.' };
    return { error: msg || `Login failed (HTTP ${resp.status})` };
  }

  let raw;
  try { raw = await resp.json(); }
  catch (_) { return { error: 'Server returned an unreadable response.' }; }
  // 2026-09-28 — TWO-STEP SIGN-IN: nothing is signed in yet; the screen asks for the code.
  if (raw && (raw.mfaRequired || raw.mfaEnrolRequired)) return { mfa: raw };
  if (!raw || !raw.Username) return { error: 'Invalid username or password.' };
  await absorbSessionToken(raw); // native shell: store bearer session, strip from payload
  // The one-time hand-over address is for this moment only — never cached with the user.
  const adoptUrl = raw.suiteAdoptUrl;
  delete raw.suiteAdoptUrl;

  // Normalise both upper- and lower-case fields so consumers can use
  // either convention. The owner-of-report logic prefers lowercase.
  const merged = {
    ...raw,
    id:        raw.Id || raw.id,
    username:  raw.Username || raw.username,
    name:      [raw.FirstName, raw.LastName].filter(Boolean).join(' ') || raw.Name || raw.name,
    email:     raw.Email || raw.email,
    role:      raw.Role || raw.role,
    company:   raw.CompanyName || raw.company,
    isStaff:   raw.IsStaff != null ? !!raw.IsStaff : !!raw.isStaff,
  };
  setCurrentUser(merged);
  // Signed in here: the browser passes once through Synerflo ID, which keeps
  // this sign-in and sends it straight back (the other apps then open signed
  // in). The page is leaving, so this never resolves and the screen stays on
  // "Signing in…". Nothing to hand over: carry on, signed in here.
  if (SSO_AUTOLOGIN && !isNativeShell() && handOver(adoptUrl)) await new Promise(() => {});
  return { user: sanitise(merged) };
}

export function getCurrentUser() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_e) {
    return null;
  }
}

export function setCurrentUser(user) {
  // Stamped: every write here is a moment the server answered for this person
  // (sign-in, sign-up, reset, /api/auth-me, an account save). See signInNeedsCheck.
  const safe = sanitise(user);
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(safe ? { ...safe, verifiedAt: Date.now() } : safe)); }
  catch (_e) { /* ignore */ }
}

// 2026-10-19 — A CACHE THE SERVER HAS NOT VOUCHED FOR LATELY IS CHECKED BEFORE
// THE APP OPENS ON IT. Brett opened a report from InSpec days after his last
// visit: the window still held his name, so the report asked for its pins
// before anything checked the sign-in, and the refusals came up as errors.
// Unstamped (written before this) counts as not vouched for: one check, then
// stamped by fetchMe.
export const CHECK_FIRST_MS = 12 * 60 * 60 * 1000;
export function signInNeedsCheck(u, now = Date.now()) {
  if (!u) return false;
  const t = Number(u.verifiedAt);
  return !(t > 0 && Math.abs(now - t) < CHECK_FIRST_MS);
}

export function clearCurrentUser() {
  try { localStorage.removeItem(STORAGE_KEY); } catch (_e) {}
}

// v0.31.0 — SaaS auth: signup + forgot/reset. All three POST to the
// new API endpoints and (where applicable) persist the returned user
// so the caller can land in a signed-in state without a second login.

// v0.45.17 — `tier` ('free' | 'pro') + optional company/phone fields
//            forwarded to the API. Stripe wiring is a follow-up rev;
//            for now the server can record the requested tier on the
//            tenant and (for pro) flag it for billing setup later.
export async function signUp({ email, password, firstName, lastName, phone, company, tier }) {
  let resp;
  try {
    resp = await fetch('/api/auth-signup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email, password, firstName, lastName, phone, company, tier,
        // Native shell only: ask for the session token in the response body.
        ...(isNativeShell() ? { tokenSession: true } : {}),
      }),
    });
  } catch (_e) {
    return { error: "Can't reach the DropPin server. Check your connection and try again." };
  }
  let body = null;
  try { body = await resp.json(); } catch (_) {}
  if (!resp.ok) return { error: (body && body.error) || `Sign-up failed (HTTP ${resp.status})` };
  if (!body || !body.Email) return { error: 'Server returned an unreadable response.' };
  await absorbSessionToken(body); // native shell: store bearer session, strip from payload

  // Mirror signIn()'s shape so consumers don't need to special-case.
  const merged = {
    ...body,
    id:       body.Id,
    username: body.Email,
    name:     [body.FirstName, body.LastName].filter(Boolean).join(' ') || body.Email,
    email:    body.Email,
    role:     body.Role || 'Owner',
  };
  setCurrentUser(merged);
  return { user: sanitise(merged) };
}

export async function requestPasswordReset(email) {
  try {
    const resp = await fetch('/api/auth-forgot-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const body = await resp.json().catch(() => ({}));
    if (!resp.ok) return { error: body.error || `Request failed (HTTP ${resp.status})` };
    return { ok: true, message: body.message };
  } catch (_e) {
    return { error: "Can't reach the DropPin server. Try again in a moment." };
  }
}

export async function resetPassword(token, password) {
  let resp;
  try {
    resp = await fetch('/api/auth-reset-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token, password,
        // Native shell only: ask for the session token in the response body.
        ...(isNativeShell() ? { tokenSession: true } : {}),
      }),
    });
  } catch (_e) {
    return { error: "Can't reach the DropPin server. Try again in a moment." };
  }
  const body = await resp.json().catch(() => ({}));
  if (!resp.ok) return { error: body.error || `Reset failed (HTTP ${resp.status})` };
  // 2026-09-28 — two-step sign-in: the password IS set; the code finishes signing in.
  if (body.mfaRequired || body.mfaEnrolRequired) return { mfa: body };
  if (!body.Email) return { error: 'Server returned an unreadable response.' };
  await absorbSessionToken(body); // native shell: store bearer session, strip from payload
  const merged = {
    ...body,
    id:       body.Id,
    username: body.Email,
    name:     [body.FirstName, body.LastName].filter(Boolean).join(' ') || body.Email,
    email:    body.Email,
    role:     body.Role || 'Owner',
  };
  setCurrentUser(merged);
  return { user: sanitise(merged) };
}

// ── v0.48.0 — cookie-session helpers ─────────────────────────────────
// The InSite-ID JWT lives in an httpOnly cookie set by /api/auth-login
// (and signup / reset-password); the localStorage user above is now
// just a UI cache. fetchMe() asks the API who the cookie belongs to:
//   { user }            signed in (localStorage cache refreshed)
//   { signedOut: true } 401/403 — cookie missing, expired or no access
//   { offline: true }   network failure — keep the cached session so
//                       the offline-first PWA doesn't dump a working
//                       session over a dropped connection.
export async function fetchMe() {
  let resp;
  try {
    resp = await fetch('/api/auth-me', { credentials: 'same-origin' });
  } catch (_e) {
    return { offline: true };
  }
  if (resp.status === 401 || resp.status === 403) return { signedOut: true };
  if (!resp.ok) return { offline: true };
  let body = null;
  try { body = await resp.json(); } catch (_) { return { offline: true }; }
  const raw = body && body.user;
  if (!raw || !raw.Username) return { signedOut: true };
  const merged = {
    ...raw,
    id:        raw.Id || raw.id,
    username:  raw.Username || raw.username,
    name:      [raw.FirstName, raw.LastName].filter(Boolean).join(' ') || raw.Name || raw.name,
    email:     raw.Email || raw.email,
    role:      raw.Role || raw.role,
    company:   raw.CompanyName || raw.company,
    isStaff:   raw.IsStaff != null ? !!raw.IsStaff : !!raw.isStaff,
  };
  setCurrentUser(merged);
  return { user: sanitise(merged) };
}

// POST /api/auth-logout — clears the httpOnly session cookie. Always
// resolves; a network failure still lets the caller clear local state.
export async function signOutServer() {
  try { await fetch('/api/auth-logout', { method: 'POST', credentials: 'same-origin' }); } catch (_e) {}
  // Native shell: drop the stored bearer session (no-op on the web — nothing
  // is ever stored there). AFTER the logout call so the request could still
  // carry its header.
  try { await clearNativeToken(); } catch (_e) { /* best-effort */ }
  // Signed out on purpose: this tab does not ask Synerflo ID straight back in.
  // (/api/auth-logout has already ended this sign-in in every app.)
  rememberAsked();
}

// -- One sign-in across the suite ---------------------------------------
// v0.246.0 (2026-09-29). Opened by someone not signed in here, DropPin asks
// Synerflo ID once (AppShell → utils/suiteSignIn askSuite); a sign-in here is
// handed on (postSignIn → handOver). It replaces July's shared insite_sso
// cookie (/api/auth-sso, /api/auth-sso-seed), which the hosting edge strips.
// false = DropPin signs in on its own, as before July.
export const SSO_AUTOLOGIN = true;
